import React from "react";
import { Routes, Route, useLocation, Link } from "react-router-dom";
import { Toaster } from "react-hot-toast";

// Layouts
import Header from "./layouts/Header";
import Footer from "./layouts/Footer";
import AdminLayout from "./layouts/AdminLayout";

// Home sections
import OfferBanner from "./layouts/components/OfferBanner";
import Banners from "./layouts/components/Banners";
import Brands from "./layouts/components/Brands";
import Trending from "./layouts/components/Trending";
import OfferSection from "./layouts/components/OfferSection";
import NewCollection from "./layouts/components/NewCollection";
import BrandsSlider, { useHotOffersCheck } from "./layouts/components/BrandsSlider";

// Pages
import BrandsPage from "./pages/BrandsPage";
import BrandDetailsPage from "./pages/BrandDetailsPage";
import CategoriesPage from "./pages/CategoriesPage";
import CategoryDetailsPage from "./pages/CategoryDetailsPage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import OffersPage from "./pages/OffersPage";
import ContactPage from "./pages/ContactPage";
import CommunityPage from "./pages/CommunityPage";
import ServicePage from "./pages/ServicePage";
import AboutPage from "./pages/AboutPage";
import SalesPolicyPage from "./pages/SalesPolicyPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ExchangePolicyPage from "./pages/ExchangePolicyPage";
import AskMariamlyPolicyPage from "./pages/AskMariamlyPolicyPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import WishlistPage from "./pages/WishlistPage";
import AccountPage from "./pages/AccountPage";
import NotFoundPage from "./pages/NotFoundPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCategoriesPage from "./pages/AdminCategoriesPage";
import AdminProductsPage from "./pages/AdminProductsPage";
import AdminOrdersPage from "./pages/AdminOrdersPage";
import AdminOrderDetailsPage from "./pages/AdminOrderDetailsPage";
import AdminBannersPage from "./pages/AdminBannersPage";
import AdminPagesBannersPage from "./pages/AdminPagesBannersPage";
import AdminBrandsPage from "./pages/AdminBrandsPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminContactInfoPage from "./pages/AdminContactInfoPage";
import AdminSubcategoriesPage from "./pages/AdminSubcategoriesPage";
import AdminNewsletterSubscriptionsPage from "./pages/AdminNewsletterSubscriptionsPage";
import AdminVariantsPage from "./pages/AdminVariantsPage";
import AdminOfferPage from "./pages/AdminOfferPage";
import AdminOfferSectionPage from "./pages/AdminOfferSectionPage";
import AdminReviewsPage from "./pages/AdminReviewsPage";
import AdminZonesPage from "./pages/AdminZonesPage";
// ————————————————————————————————
// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// ————————————————————————————————
// Home page (يحافظ على ترتيب المقاطع اللي عندك)
function Home() {
  const { hasHotOffers, isChecking } = useHotOffersCheck();

  return (
    <>
      {/* Offer Banner Line */}
      <OfferBanner />
      
      {/* Full-bleed banners below the offer line */}
      <div className="bleed">
        <Banners />
      </div>

      {/* Brands section inside container */}
      <div className="container">
        <Brands />
      </div>

      {/* Page main content with container + spacing */}
      <main className="container page">
        <NewCollection />
        {/* Only render BrandsSlider if there are hot offers */}
        {hasHotOffers && <BrandsSlider />}
        <Trending />
        <OfferSection />
      </main>
    </>
  );
}

// ————————————————————————————————
export default function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  if (isAdminRoute) {
    return (
      <>
        <ScrollToTop />
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/categories" element={<AdminCategoriesPage />} />
          <Route path="/admin/subcategories" element={<AdminSubcategoriesPage />} />
          <Route path="/admin/products" element={<AdminProductsPage />} />
          <Route path="/admin/variants" element={<AdminVariantsPage />} />
          <Route path="/admin/orders" element={<AdminOrdersPage />} />
          <Route path="/admin/orders/:orderId" element={<AdminOrderDetailsPage />} />
            <Route path="/admin/banners" element={<AdminBannersPage />} />
            <Route path="/admin/pages-banners" element={<AdminPagesBannersPage />} />
          <Route path="/admin/brands" element={<AdminBrandsPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/contact-info" element={<AdminContactInfoPage />} />
          <Route path="/admin/newsletter-subscriptions" element={<AdminNewsletterSubscriptionsPage />} />
          <Route path="/admin/offers" element={<AdminOfferPage />} />
          <Route path="/admin/offer-sections" element={<AdminOfferSectionPage />} />
          <Route path="/admin/reviews" element={<AdminReviewsPage />} />
          <Route path="/admin/zones" element={<AdminZonesPage />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
        </Routes>
        <Toaster position="top-right" />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Header />

      <Routes>
        {/* Home */}
        <Route path="/" element={<Home />} />

        {/* Brands */}
        <Route path="/brands" element={<BrandsPage />} />
        <Route path="/brands/:slug" element={<BrandDetailsPage />} />

        {/* Categories */}
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/categories/:slug" element={<CategoryDetailsPage />} />

        {/* Shop + Product */}
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/product/:idOrSlug" element={<ProductPage />} />

        {/* Static-ish */}
        <Route path="/offers" element={<OffersPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/services" element={<ServicePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/sales-policy" element={<SalesPolicyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/exchange-policy" element={<ExchangePolicyPage />} />
        <Route path="/ask-mariamly-policy" element={<AskMariamlyPolicyPage />} />

        {/* User */}
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/account" element={<AccountPage />} />
        
        {/* 404 */}
        <Route
          path="*"
          element={
            <NotFoundPage />
            // لو بدك صفحة 404 سريعة بدون ملف منفصل، استبدل السطرين بمحتوى بسيط:
            // <section className="container page" style={{ padding: 24 }}>
            //   <h1>404</h1>
            //   <p>Page not found.</p>
            //   <Link to="/" style={{ color: "#0f62fe" }}>Back to Home</Link>
            // </section>
          }
        />
      </Routes>

      <Footer />
      <Toaster position="top-right" />
    </>
  );
}
